<script lang="ts">
// Packages
import { defineComponent, type PropType } from 'vue';
import type { SearchClient } from 'algoliasearch/lite';
import algoliasearch from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisConfigure,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisSearchBox,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';

type RefinementList = { [key: string]: string[] };

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisConfigure,
    AisHits,
    AisInstantSearch,
    AisPagination,
    AisSearchBox,
    RefinementPanel,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    currentCategory: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
    disableCategoryFilter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      algoliaClient: null as SearchClient | null,
      searchClient: null as SearchClient | null,
    };
  },
  computed: {
    initialFilter() {
      // Read type filter from URL query parameters (only if category filtering is enabled)
      if (this.disableCategoryFilter) {
        return '';
      }
      const urlParams = new URLSearchParams(window.location.search);
      const typeFilter = urlParams.get('type');
      return typeFilter ? `type:"${typeFilter}"` : '';
    },
    routing() {
      const { indexName, currentCategory, refinements, disableCategoryFilter } = this as {
        indexName: string;
        currentCategory: string;
        refinements: Refinement[];
        disableCategoryFilter: boolean;
      };

      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page && page > 1) {
              route.p = page.toString();
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            // Always read from URL query parameters directly to ensure we get the latest state
            const urlParams = new URLSearchParams(window.location.search);

            const refinementList = Object.fromEntries(
              refinements
                .map(({ attribute }) => {
                  // Prioritize URL params over routeState to ensure we get the actual URL state
                  const urlValue = urlParams.get(attribute);
                  const routeValue = routeState[attribute];
                  const value = urlValue || routeValue || undefined;
                  return [
                    attribute,
                    value ? (Array.isArray(value) ? value : value.split('~')) : undefined,
                  ];
                })
                .filter(([, val]) => val !== undefined),
            );

            // Force the insight category for the current entry to be pre-selected (only if filtering is enabled)
            if (!disableCategoryFilter) {
              if (!refinementList.insightCategories) {
                refinementList.insightCategories = [currentCategory];
              } else if (!refinementList.insightCategories.includes(currentCategory)) {
                refinementList.insightCategories.push(currentCategory);
              }
            }

            const state = {
              [indexName]: {
                query: routeState.q,
                refinementList,
                page: Number(routeState.p),
              },
            };

            return state;
          },
        },
      };
    },
  },
  created() {
    this.algoliaClient = algoliasearch(this.appId, this.searchApiKey);
    this.searchClient = {
      ...this.algoliaClient,
      search: (requests: Parameters<SearchClient['search']>[0]) => {
        // Do not prevent the search request if the query is empty as we want to show the results immediately on a category (article index) template
        // Make the search request to Algolia
        return this.algoliaClient!.search(requests);
      },
    };
  },
  methods: {
    onPageChange() {
      this.$el.scrollIntoView();
    },
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      const { searchInput } = searchBox.$refs;
      const { input } = searchInput.$refs;
      // Focus the search input (using autofocus is only reliable on page load)
      input.focus();
    },
  },
});
</script>
<template>
  <ais-instant-search :search-client="searchClient" :routing="routing" :index-name="indexName">
    <ais-configure
      :hits-per-page.camel="9"
      :distinct="true"
      :filters="initialFilter"
    ></ais-configure>
    <div class="flex flex-col md:flex-row">
      <!-- ====== START REFINEMENTS ====== -->
      <div class="bg-gtg-pink-700 px-8 py-8 text-white md:basis-[21.5rem]">
        <h3 class="mb-4 font-alecrim text-2xl font-black italic">Filter &amp; search</h3>

        <!-- ====== START SEARCH BOX ====== -->
        <div
          class="shadow-default rounded-lg bg-white text-gtg-claret focus-within:ring-2 focus-within:ring-gtg-teal-500 focus-within:ring-offset-4 focus-within:ring-offset-gtg-pink-700 [&.v-enter-active]:transition-opacity [&.v-enter-from]:opacity-0 [&.v-leave-active]:transition-opacity [&.v-leave-to]:opacity-0"
        >
          <ais-search-box
            ref="searchBox"
            show-loading-indicator
            placeholder="Search articles"
            class="mb-7"
            :class-names="{
              'ais-SearchBox-form': 'relative',
              'ais-SearchBox-input':
                'bg-transparent lg:pl-4 lg:pr-15.5 py-3 border-0 focus:outline-none focus:ring-0 w-full px-4 placeholder:text-gtg-claret [&::-webkit-search-cancel-button]:hidden',
              'ais-SearchBox-loadingIndicator': 'hidden',
              'ais-SearchBox-reset': '-translate-y-1/2 absolute right-11 top-1/2',
              'ais-SearchBox-submit':
                '-translate-y-1/2 absolute right-4 top-1/2 focus-visible:text-gtg-teal-500',
            }"
          >
            <template #submit-icon>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                @click.prevent="focusSearchInput"
              >
                <path
                  d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
                  stroke="#161818"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </ais-search-box>
        </div>
        <!-- ====== END SEARCH BOX ====== -->

        <div class="mb-6">
          <refinement-panel
            v-for="refinement in refinements"
            :key="refinement.attribute"
            v-bind="refinement"
          />
        </div>
      </div>
      <!-- ====== END REFINEMENTS ====== -->

      <div class="grow">
        <!-- ====== START HITS ====== -->
        <ais-hits
          :class-names="{
            'ais-Hits': 'container max-w-slim my-[3.75rem]',
            'ais-Hits-list':
              'grid gap-x-8 gap-y-16 grid-cols-[repeat(auto-fit,20rem)] justify-center overflow-hidden mb-8',
            'ais-Hits-item': 'mx-auto',
          }"
        >
          <template #item="props">
            <slot name="item" v-bind="props" />
          </template>
        </ais-hits>
        <!-- ====== END HITS ====== -->

        <!-- ====== START PAGINATION ====== -->
        <ais-pagination
          :class-names="{
            'ais-Pagination-list': 'flex items-center justify-center mb-10 text-lg sm:text-xl',
            'ais-Pagination-item': 'text-base rounded-full',
            'ais-Pagination-item--previousPage': 'bg-gtg-pink-300 text-gtg-claret mr-3',
            'ais-Pagination-item--nextPage': 'bg-gtg-pink-300 text-gtg-claret ml-3',
            'ais-Pagination-item--selected': 'bg-gtg-pink-300 text-white',
            'ais-Pagination-item--disabled': 'cursor-not-allowed opacity-60',
            'ais-Pagination-link': 'size-8 grid place-content-center',
          }"
          :show-first="false"
          :show-last="false"
          @page-change="onPageChange"
        >
          <template #previous="{ isFirstPage, createURL, refine }">
            <a
              v-if="!isFirstPage"
              class="ais-Pagination-link grid size-10 place-content-center"
              aria-label="Previous page"
              :href="createURL()"
              @click.exact.left.prevent="refine()"
              ><svg
                width="11"
                height="18"
                viewBox="0 0 11 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M10 17L2 9L10 1"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                /></svg
            ></a>
            <span
              v-else
              class="ais-Pagination-link grid size-10 place-content-center"
              aria-label="Previous page"
              ><svg
                width="11"
                height="18"
                viewBox="0 0 11 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M10 17L2 9L10 1"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                /></svg
            ></span>
          </template>
          <template #next="{ isLastPage, createURL, refine }">
            <a
              v-if="!isLastPage"
              class="ais-Pagination-link grid size-10 place-content-center"
              aria-label="Next page"
              :href="createURL()"
              @click.exact.left.prevent="refine()"
              ><svg
                width="11"
                height="18"
                viewBox="0 0 11 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1 1L9 9L0.999997 17"
                  stroke="#161818"
                  stroke-width="1.5"
                  stroke-linecap="round"
                /></svg
            ></a>
            <span
              v-else
              class="ais-Pagination-link grid size-10 place-content-center"
              aria-label="Next page"
              ><svg
                width="11"
                height="18"
                viewBox="0 0 11 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1 1L9 9L0.999997 17"
                  stroke="#161818"
                  stroke-width="1.5"
                  stroke-linecap="round"
                /></svg
            ></span>
          </template>
        </ais-pagination>
        <!-- ====== END PAGINATION ====== -->
      </div>
    </div>
  </ais-instant-search>
</template>
